import { useCallback } from 'react'
import {
  EmploymentType,
  ShopSessionDocument,
  useProductOffersEmploymentTypeUpdateMutation,
} from '@/services/graphql/generated'
import { logger } from '@/services/logger/logger.client'
import { useOptimisticValue } from '@/utils/useOptimisticValue'
import { isUnsupportedEmploymentType } from './employmentType.utils'

type Params = {
  productOfferIds: Array<string>
  initialValue?: EmploymentType
}

export function useOptimisticEmploymentType({ productOfferIds, initialValue }: Params) {
  const [employmentType, setEmploymentType, revertToOriginalEmploymentType] = useOptimisticValue(initialValue)

  const [updateEmploymentType, { loading }] = useProductOffersEmploymentTypeUpdateMutation({
    // Employment type affects the price, so the cart total has to be recalculated. The updated
    // offers come back on the mutation itself, but the cart cost is computed server side.
    refetchQueries: [ShopSessionDocument],
    awaitRefetchQueries: true,
    onCompleted(data) {
      if (data.productOffersEmploymentTypeUpdate.userError) {
        logger.error('Failed to update employment type', {
          userError: data.productOffersEmploymentTypeUpdate.userError,
        })
        revertToOriginalEmploymentType()
      }
    },
    onError(error) {
      logger.error('Failed to update employment type', { error })
      revertToOriginalEmploymentType()
    },
  })

  const handleSetEmploymentType = useCallback(
    (value: EmploymentType) => {
      setEmploymentType(value)

      // Unsupported employment types are set locally only — underwriter rejects them, and reverting on that
      // error would hide the warning the customer needs to see.
      if (isUnsupportedEmploymentType(value)) {
        return
      }

      updateEmploymentType({ variables: { productOfferIds, employmentType: value } })
    },
    [productOfferIds, updateEmploymentType, setEmploymentType],
  )

  return [employmentType, handleSetEmploymentType, loading] as const
}
